import axios from 'axios';
import { logger } from '@/lib/logger';
import type { PricesResponse } from '@/types/database';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const publicClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

interface Envelope<T> {
  success: boolean;
  data: T;
}

function statusOf(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined;
}

export async function getPricesPublic(): Promise<PricesResponse> {
  logger.debug('[landing] GET /api/subscription/prices');
  try {
    const res = await publicClient.get<Envelope<PricesResponse>>(
      '/api/subscription/prices'
    );
    logger.info('[landing] prices loaded', {
      monthly: res.data.data.monthly.display_price,
      yearly: res.data.data.yearly.display_price,
    });
    return res.data.data;
  } catch (err) {
    logger.error('[landing] prices fetch failed', err, { status: statusOf(err) });
    throw err;
  }
}

export type SampleQuestion = {
  id: number;
  question_text: string;
  category: string;
  hint: string | null;
  is_65_20: boolean;
};

export type SampleCheckResponse = {
  is_correct: boolean;
  user_answer: string;
  correct_answer: string;
  explanation: string;
};

interface SampleQuestionEnvelope {
  question: SampleQuestion;
}

export async function getSampleQuestion(): Promise<SampleQuestion> {
  logger.debug('[landing] GET /api/questions/sample');
  try {
    const res = await publicClient.get<Envelope<SampleQuestionEnvelope>>(
      '/api/questions/sample'
    );
    const q = res.data.data.question;
    logger.info('[landing] sample question loaded', {
      id: q.id,
      category: q.category,
    });
    return q;
  } catch (err) {
    logger.error('[landing] sample question fetch failed', err, {
      status: statusOf(err),
    });
    throw err;
  }
}

export async function checkSampleAnswer(params: {
  question_id: number;
  user_answer: string;
}): Promise<SampleCheckResponse> {
  logger.debug('[landing] POST /api/questions/sample/check', {
    question_id: params.question_id,
    answer_length: params.user_answer.length,
  });
  try {
    const res = await publicClient.post<Envelope<SampleCheckResponse>>(
      '/api/questions/sample/check',
      params
    );
    logger.info('[landing] sample answer graded', {
      question_id: params.question_id,
      is_correct: res.data.data.is_correct,
    });
    return res.data.data;
  } catch (err) {
    logger.error('[landing] sample answer grading failed', err, {
      status: statusOf(err),
      question_id: params.question_id,
    });
    throw err;
  }
}

interface TranscriptionBody {
  success?: boolean;
  transcript?: string;
  confidence?: number;
  data?: {
    transcript?: string;
    confidence?: number;
  };
}

export async function transcribeSampleAudio(
  audioBlob: Blob,
): Promise<{ transcript: string; confidence?: number }> {
  logger.debug('[landing] POST /api/transcribe', {
    size: audioBlob.size,
    type: audioBlob.type,
  });
  const formData = new FormData();
  const extension = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
  formData.append('audio', audioBlob, `recording.${extension}`);

  try {
    const res = await publicClient.post<TranscriptionBody>(
      '/api/transcribe',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    const transcript = res.data.data?.transcript ?? res.data.transcript ?? '';
    const confidence = res.data.data?.confidence ?? res.data.confidence;
    logger.info('[landing] sample audio transcribed', {
      length: transcript.length,
    });
    return { transcript, confidence };
  } catch (err) {
    logger.error('[landing] sample audio transcription failed', err, {
      status: statusOf(err),
    });
    throw err;
  }
}

export interface JoinWaitlistResponse {
  email: string;
  already_subscribed: boolean;
}

// first_name / last_name / interview_date are pending backend support —
// POST /api/waitlist only accepts `email` today.
export interface JoinWaitlistRequest {
  email: string;
  first_name?: string;
  last_name?: string;
  interview_date?: string;
}

export async function joinWaitlist(
  params: JoinWaitlistRequest
): Promise<JoinWaitlistResponse> {
  logger.debug('[landing] POST /api/waitlist', { email_length: params.email.length });
  try {
    const res = await publicClient.post<Envelope<JoinWaitlistResponse>>(
      '/api/waitlist',
      params
    );
    logger.info('[landing] waitlist join succeeded', {
      already_subscribed: res.data.data.already_subscribed,
    });
    return res.data.data;
  } catch (err) {
    logger.error('[landing] waitlist join failed', err, { status: statusOf(err) });
    throw err;
  }
}

/**
 * Pull `error.code` out of a public-API axios failure. The landing endpoints
 * return `{ success: false, error: { message, code } }` on 4xx/5xx; if the
 * request never reached the server (network drop, CORS, etc.) we have no
 * code, so callers pass a domain-scoped fallback (e.g. 'landing_grade_failed')
 * to map through `getLandingError` at the call site.
 */
export function extractLandingErrorCode(
  err: unknown,
  fallback: string,
): string {
  if (axios.isAxiosError(err)) {
    const code = err.response?.data?.error?.code;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  return fallback;
}
